import React from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import { withConfluenceStyleWrapper } from '../confluenceStyleWrapper';
import { getPlotMargin, type MarginValue } from '../getPlotMargin';
import { fg } from '@atlaskit/platform-feature-flags';
import type { ThemeElementProps } from '@atlassian/viz-platform-charts/primitives';

const PlatformLineChart = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_linechart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.LineChart })),
);

const Title = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_linechart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.LineChart.elements.Title })),
);

const SubTitle = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_linechart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.LineChart.elements.SubTitle })),
);

const Layout = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_barchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.LineChart.elements.Layout })),
);

const Theme = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_linechart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.LineChart.elements.Theme })),
);

type PlatformLineChartProps = React.ComponentProps<typeof PlatformLineChart>;

/**
 * Excluded props:
 * - children - we will not be supporting primitives/elements to compose the chart
 * - colorPalette - allow the user to match color to attribute
 *
 * Limited props:
 * - xAccessor - only allow non function types
 * - yAccessor - only allow non function types
 * - colorAccessor - only allow non function types
 */

export type LineChartProps = Pick<PlatformLineChartProps, 'width' | 'height' | 'data'> & {
	colorAccessor?: number | string;
	colorPalette?: ThemeElementProps['fillStyles'];
	plotMargin?: MarginValue;
	plotMarginBottom?: MarginValue;
	plotMarginLeft?: MarginValue;
	plotMarginRight?: MarginValue;
	plotMarginTop?: MarginValue;
	showBorder?: boolean;
	subtitle?: string;
	title?: string;
	xAccessor: number | string;
	yAccessor: number | string;
};

const BaseLineChart = (props: Parameters<RenderFn>[0]) => {
	const {
		width,
		height,
		data,
		xAccessor,
		yAccessor,
		colorAccessor,
		title,
		subtitle,
		plotMargin,
		plotMarginTop,
		plotMarginRight,
		plotMarginBottom,
		plotMarginLeft,
		showBorder,
		colorPalette,
	} = props.forgeDoc.props as LineChartProps;

	return (
		<PlatformLineChart
			width={width}
			height={height ? height : 400}
			data={data}
			xAccessor={xAccessor}
			yAccessor={yAccessor}
			colorAccessor={colorAccessor}
		>
			{title && <Title>{title}</Title>}
			{subtitle && <SubTitle>{subtitle}</SubTitle>}
			{fg('enable-margin-property-for-ui-kit-chart-components') && (
				<Layout
					plotMargin={getPlotMargin(
						plotMargin,
						plotMarginLeft,
						plotMarginRight,
						plotMarginTop,
						plotMarginBottom,
					)}
					showBorder={showBorder}
				/>
			)}
			{colorPalette && fg('platform-enable-customisation-colors-for-charts') && (
				<Theme fillStyles={colorPalette} />
			)}
		</PlatformLineChart>
	);
};

export const LineChart: (props: Parameters<RenderFn>[0]) => JSX.Element =
	withConfluenceStyleWrapper(BaseLineChart);
